/**
 * Chọn CÔNG TY LẤY DỮ LIỆU cho một widget dashboard (widgetConfig.id_apps — xem dashboardCongTy.js).
 *   - ChonCongTyWidget: ô chọn dùng lại trong hộp "Thêm Widget".
 *   - mặc định: hộp thoại đổi công ty của widget đã có (nút ở chế độ sửa bố cục).
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert, Box, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import Dialog from './ManagedModal';
import ManagedDropdown from './ManagedDropdown';
import { useDsCongTy } from './PhamViCongTy';
import { MA_CONG_TY_HIEN_TAI, hoTroNhieuCongTy } from './dashboardCongTy';

const MUC_HIEN_TAI = { _id: MA_CONG_TY_HIEN_TAI, name: 'Công ty đang đăng nhập' };

/** Câu giải thích widget sẽ hiện thế nào với lựa chọn hiện tại. */
const moTa = (type, soCongTy) => {
	if (soCongTy === 0) return 'Để trống: widget hiện dữ liệu của công ty đang đăng nhập (như trước đây).';
	if (soCongTy === 1) return 'Widget luôn hiện dữ liệu của công ty đã chọn, dù bạn đang đăng nhập công ty nào.';
	if (type === 'counter') return 'Thẻ đếm sẽ CỘNG TỔNG số của các công ty đã chọn; rê chuột lên số để xem từng công ty.';
	return 'Widget có thanh chọn công ty ở đầu; bấm vào tên công ty để xem dữ liệu của công ty đó.';
};

export function ChonCongTyWidget({ type, value, onChange, enabled = true }) {
	const { dsCongTy, isLoading } = useDsCongTy(enabled);
	const options = useMemo(() => [MUC_HIEN_TAI, ...dsCongTy], [dsCongTy]);
	const chon = useMemo(() => (value || []).map((id) => options.find((o) => o._id === String(id)) || { _id: String(id), name: String(id) }), [value, options]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			<ManagedDropdown
				type="autocomplete"
				multiple
				loading={isLoading}
				options={options}
				value={chon}
				disableCloseOnSelect
				isOptionEqualToValue={(o, v) => o._id === v._id}
				getOptionLabel={(o) => o.name || o._id}
				onChange={(e, v) => onChange((v || []).map((o) => o._id))}
				renderTags={(vals, getTagProps) => vals.map((o, i) => (
					<Chip {...getTagProps({ index: i })} key={o._id} size="small" icon={<BusinessIcon />} label={o.name || o._id} />
				))}
				renderInput={(params) => (
					<TextField {...params} label="Lấy dữ liệu của công ty" placeholder={chon.length ? '' : 'Công ty đang đăng nhập'} />
				)}
				noOptionsText="Không có công ty nào"
				fullWidth
			/>
			<Typography variant="caption" color="text.secondary">{moTa(type, chon.length)}</Typography>
		</Box>
	);
}

ChonCongTyWidget.propTypes = {
	type: PropTypes.string,
	value: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func.isRequired,
	enabled: PropTypes.bool,
};

export default function DynamicWidgetCongTyDialog({ open, widget, onClose, onSave }) {
	const [ds, setDs] = useState([]);
	useEffect(() => {
		if (open && widget) {
			const raw = widget.widgetConfig && widget.widgetConfig.id_apps;
			setDs(Array.isArray(raw) ? raw.map(String) : []);
		}
	}, [open, widget]);

	if (!widget) return null;
	const hoTro = hoTroNhieuCongTy(widget);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle fontWeight="bold">Công ty lấy dữ liệu — {widget.title}</DialogTitle>
			<DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{hoTro ? (
					<>
						<ChonCongTyWidget type={widget.type} value={ds} onChange={setDs} enabled={open} />
						<Alert severity="info" sx={{ fontSize: '0.8rem' }}>
							Chỉ hiện được dữ liệu của công ty mà tài khoản của bạn có quyền. Người khác mở dashboard này mà
							không có quyền ở công ty đó sẽ thấy thông báo lỗi trong widget, không thấy số liệu.
						</Alert>
					</>
				) : (
					<Alert severity="warning">
						Loại widget này luôn làm việc với công ty đang đăng nhập (lối tắt, nút thêm mới, quy trình, flow).
					</Alert>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2 }}>
				<Button onClick={onClose} color="inherit">Hủy</Button>
				<Button variant="contained" disabled={!hoTro} onClick={() => onSave(ds)}>Áp dụng</Button>
			</DialogActions>
		</Dialog>
	);
}

DynamicWidgetCongTyDialog.propTypes = {
	open: PropTypes.bool,
	widget: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
};
